import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { explain_lines, has_explain, popover_position } from './drilldown_logic';

/*
 * "Why this amount?" popover. Renders the explanation the ENGINE PERSISTED (item/penalty detail,
 * monthly_explain) verbatim; nothing is recomputed here.
 */
const POP_WIDTH = 320;
const HIDDEN = { top: -9999, left: -9999, visibility: 'hidden' };

const WhyPopover = ({ detail, amount_node, label = 'Why this amount?' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const trigger_ref = useRef(null);
  const pop_ref = useRef(null);
  const close_ref = useRef(null);

  const close = useCallback((restore_focus) => {
    setOpen(false);
    setPos(null);
    if (restore_focus && trigger_ref.current) trigger_ref.current.focus({ preventScroll: true });
  }, []);

  // Measure after the hidden first paint, then place (and flip upward near the viewport bottom).
  useLayoutEffect(() => {
    if (!open || !trigger_ref.current || !pop_ref.current) return;
    const rect = trigger_ref.current.getBoundingClientRect();
    setPos(
      popover_position(
        rect,
        pop_ref.current.offsetHeight,
        window.innerWidth,
        window.innerHeight,
        POP_WIDTH
      )
    );
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    if (close_ref.current) close_ref.current.focus({ preventScroll: true });
    // capture phase + stopPropagation, so Escape closes only this popover, not the CustomModal
    // around it.
    const on_key = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close(true);
    };
    // A fixed popover would detach from its amount on scroll/resize, so it closes instead.
    const on_move = () => close(false);
    document.addEventListener('keydown', on_key, true);
    window.addEventListener('resize', on_move);
    window.addEventListener('scroll', on_move, true);
    return () => {
      document.removeEventListener('keydown', on_key, true);
      window.removeEventListener('resize', on_move);
      window.removeEventListener('scroll', on_move, true);
    };
  }, [open, close]);

  if (!has_explain(detail)) return amount_node;
  const lines = explain_lines(detail);
  const style = pos
    ? { top: pos.top, left: pos.left, width: POP_WIDTH }
    : { ...HIDDEN, width: POP_WIDTH };

  return (
    <span className="tp-why">
      <button
        ref={trigger_ref}
        type="button"
        className="tp-why-trigger"
        onClick={() => (open ? close(true) : setOpen(true))}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {amount_node}
      </button>
      {open
        ? createPortal(
            <span
              ref={pop_ref}
              className="tp-why-pop"
              role="dialog"
              aria-label={label}
              style={style}
            >
              <span className="tp-why-pop-hd">
                {label}
                <button
                  ref={close_ref}
                  type="button"
                  className="tp-link"
                  aria-label="Close explanation"
                  onClick={() => close(true)}
                >
                  ✕
                </button>
              </span>
              {lines.map((l) => (
                <span className="tp-why-line" key={`${l.label}-${l.value}`}>
                  <span className="tp-why-k">{l.label}</span>
                  <span className="tp-why-v">{l.value}</span>
                </span>
              ))}
              <span className="tp-why-ft">
                Stored explanation — shown exactly as the engine saved it.
              </span>
            </span>,
            document.body
          )
        : null}
    </span>
  );
};

export default WhyPopover;
